import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography
} from '@mui/material';
import type { Sx } from '@/shared/types/uiTypes';

import { Z_INDEX } from '@/config/z-index';
import { preventScrollPropagation } from '@/shared/lib/dom/preventScrollPropagation';
import { normalizeSx } from '@/shared/lib/normalizeSx';
import { PreviewImageTooltip } from '@/shared/ui/overlays/PreviewImageTooltip';
import type { GroupedOption, Option, Options } from './types';
import useSelectMenu from './useSelectMenu';

const isGroupedOption = (option: Option | GroupedOption): option is GroupedOption => {
  return 'options' in option;
};

type GroupedSelectProps = {
  label: string;
  value: string | null;
  onChange: (value: Option) => void;
  options: Options;
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  testId?: string;
  sx?: Sx;
};

const paperBaseSx: Sx = {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  p: 3,
  width: '100%',
  gap: 1
};

const GroupedSelect = ({
  label,
  value,
  onChange,
  options,
  disabled = false,
  placeholder = '',
  isLoading = false,
  testId,
  sx
}: GroupedSelectProps) => {
  const groupedOptions = options.filter(isGroupedOption);
  const directOptions = options.filter(option => !isGroupedOption(option)) as Option[];

  const flatOptions: Option[] = [
    ...directOptions,
    ...groupedOptions.flatMap(group => group.options)
  ];

  const { handleMenuItemClick, selected } = useSelectMenu({
    options: flatOptions,
    onChange,
    value
  });

  const [openSelect, setOpenSelect] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [renderAbove, setRenderAbove] = useState(false);

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (openSelect && triggerRef.current && dropdownRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      const dropdownHeight = Math.min(300, dropdownRef.current.scrollHeight);

      // Flip above if not enough space below and more room above
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setRenderAbove(true);
        dropdownRef.current.style.maxHeight = `${spaceAbove - 10}px`;
      } else {
        setRenderAbove(false);
        dropdownRef.current.style.maxHeight = `${spaceBelow - 10}px`;
      }
    }
  }, [openSelect]);

  const handleGroupToggle = (groupLabel: string) => {
    setOpenGroup(prev => (prev === groupLabel ? null : groupLabel));
  };

  const handleClickAway = () => {
    setOpenSelect(false);
  };

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    if (openSelect && dropdownRef.current) {
      cleanup = preventScrollPropagation(dropdownRef.current);
    }

    return () => {
      if (cleanup) cleanup();
    };
  }, [openSelect]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        component={Paper}
        data-testid={testId ? `setting-changer-${testId}` : undefined}
        sx={[paperBaseSx, ...normalizeSx(sx)]}
      >
        {openSelect && (
          <Box
            sx={{
              position: 'fixed',
              width: '100vw',
              height: '100vh',
              left: 0,
              top: 0,
              background: 'rgba(18, 22, 63, 0.40)',
              zIndex: Z_INDEX.menu
            }}
            onClick={handleClickAway}
          />
        )}
        <Typography
          className='label__container'
          sx={{
            width: {
              xs: 'auto',
              md: '40%'
            }
          }}
          variant='h6'
        >
          {label}
        </Typography>
        <Box sx={{ minWidth: '250px', position: 'relative' }}>
          <Box
            ref={triggerRef}
            data-testid={testId ? `select-${testId}` : undefined}
            sx={theme => ({
              border: `1px solid ${disabled ? theme.palette.action.disabled : theme.palette.gx.text}`,
              paddingLeft: 2,
              paddingRight: 2,
              cursor: disabled || isLoading ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: '26px',
              color: disabled ? theme.palette.gx.textDisabled : theme.palette.gx.text,
              backgroundColor: disabled ? theme.palette.action.disabledBackground : 'inherit'
            })}
            onClick={() => {
              if (!disabled && !isLoading) {
                const nextOpen = !openSelect;
                setOpenSelect(nextOpen);
                if (nextOpen && groupedOptions.length > 0 && selected) {
                  const selectedGroup = groupedOptions.find(group =>
                    group.options.some(option => option.value === selected.value)
                  );
                  setOpenGroup(selectedGroup?.label ?? null);
                }
              }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                overflow: 'hidden',
                fontSize: '0.875rem'
              }}
            >
              {isLoading && (
                <CircularProgress
                  data-testid={testId ? `loading-${testId}` : undefined}
                  size={16}
                  thickness={4}
                />
              )}
              <Box
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {selected ? selected.label : placeholder}
              </Box>
              {selected?.info}
            </Box>
            <ExpandMoreIcon
              sx={theme => ({
                width: '14px',
                transform: openSelect ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out',
                cursor: disabled || isLoading ? 'default' : 'pointer',
                color: disabled ? theme.palette.gx.textDisabled : 'inherit'
              })}
            />
          </Box>
          {openSelect && (
            <Box
              ref={dropdownRef}
              sx={{
                position: 'absolute',
                [renderAbove ? 'bottom' : 'top']: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: Z_INDEX.priority,
                overflowY: 'auto',
                backgroundColor: 'gx.tileDark',
                border: '1px solid rgba(0,0,0,0.12)',
                minWidth: '100%'
              }}
            >
              {directOptions.map(option => (
                <PreviewImageTooltip
                  key={option.value}
                  height={option.height}
                  previewImage={option.previewImage}
                >
                  <ListItemButton
                    selected={selected?.value === option.value}
                    sx={{ pl: 2, gap: 1, display: 'flex', fontSize: '0.875rem' }}
                    onClick={() => {
                      handleMenuItemClick(flatOptions.indexOf(option));
                      setOpenSelect(false);
                    }}
                  >
                    {option.label} {option.info}
                  </ListItemButton>
                </PreviewImageTooltip>
              ))}
              {groupedOptions.map(group => (
                <Box key={group.label}>
                  <ListItemButton
                    sx={{ fontSize: '0.875rem' }}
                    onClick={() => {
                      handleGroupToggle(group.label);
                    }}
                  >
                    <ListItemText
                      primary={group.label}
                      sx={{
                        display: 'flex',
                        m: 0,
                        '.MuiListItemText-primary': { fontSize: '14px !important' }
                      }}
                    />
                    {openGroup === group.label ? (
                      <ExpandLess sx={{ fontSize: '0.875rem' }} />
                    ) : (
                      <ExpandMoreIcon sx={{ fontSize: '0.875rem' }} />
                    )}
                  </ListItemButton>
                  <Collapse
                    unmountOnExit
                    in={openGroup === group.label}
                    timeout='auto'
                  >
                    <List disablePadding>
                      {group.options.map(option => (
                        <PreviewImageTooltip
                          key={option.value}
                          height={option.height}
                          previewImage={option.previewImage}
                        >
                          <ListItemButton
                            selected={selected?.value === option.value}
                            sx={{ pl: 4, fontSize: '0.875rem' }}
                            onClick={() => {
                              handleMenuItemClick(flatOptions.indexOf(option));
                              setOpenSelect(false);
                            }}
                          >
                            {option.label}
                          </ListItemButton>
                        </PreviewImageTooltip>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

export default GroupedSelect;
